import React from 'react';

import { useTranslation } from 'react-i18next';

import styles from './ActionsModal.module.css';
import { ModalPortal } from '../modals/ModalPortal';

export interface ModalAction {
  label: string;
  onClick: () => void;
  styleType?: 'primary' | 'secondary' | 'danger' | 'default';
}

interface UniversalModalProperties {
  isVisible: boolean;
  title?: string;
  subtitle?: string;
  message?: string;
  actions: ModalAction[];
  onClose?: () => void;
}

export const ActionsModal: React.FC<UniversalModalProperties> = ({
  isVisible,
  title,
  subtitle,
  message,
  actions,
  onClose,
}) => {
  const { t } = useTranslation('common');
  if (!isVisible) {
    return null;
  }
  const titleId = title ? 'actions-modal-title' : undefined;
  const descId = message ? 'actions-modal-description' : undefined;

  return (
    <ModalPortal>
      {/* Overlay acts as a backdrop; use a real button to handle dismissal for a11y compliance */}
      <div className={styles.modalOverlay}>
        <button
          type="button"
          aria-label={t('common.close', 'Close')}
          className={styles.overlayDismiss}
          onClick={(event_) => event_.stopPropagation()}
          onKeyDown={(event_) => {
            if (event_.key === 'Escape' && onClose) {
              onClose();
            }
          }}
        />
        <div
          className={styles.modalContainer}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          tabIndex={-1}
        >
          {(title || subtitle) && (
            <div className={styles.modalHeader}>
              {title && <h2 id={titleId}>{title}</h2>}
              {subtitle && <h4>{subtitle}</h4>}
            </div>
          )}
          {message && (
            <div className={styles.modalContent}>
              <p id={descId}>{message}</p>
            </div>
          )}
          <div className={styles.modalFooter}>
            {actions.map((action, index) => (
              <button
                key={`action-${index}`}
                onClick={action.onClick}
                className={`${styles.modalButton} ${styles[action.styleType || 'default']}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
